import React, { useState } from 'react';
import {
  fetchSheetPricingDiff,
  applySheetPricing,
  SheetPricingDiffRow,
  SheetPricingSkippedRow,
} from '../../lib/api';
import { formatModelLabel } from '../../utils/pricing';
import { formatAccountName } from '../../utils/formatters';
import './SheetImport.css';

interface SheetImportProps {
  onClose: () => void;
}

interface ApplyResult {
  applied: number;
  failed: Array<{ account?: string; date?: string; reason: string }>;
}

export const SheetImport: React.FC<SheetImportProps> = ({ onClose }) => {
  const [sheetUrl, setSheetUrl] = useState('');
  const [diffs, setDiffs] = useState<SheetPricingDiffRow[] | null>(null);
  const [skipped, setSkipped] = useState<SheetPricingSkippedRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);

  const loadDiff = async () => {
    if (!sheetUrl.trim()) {
      setError('Paste a Google Sheets URL above.');
      return;
    }
    setError(null);
    setApplyResult(null);
    setLoading(true);
    try {
      const result = await fetchSheetPricingDiff(sheetUrl.trim());
      setDiffs(result.diffs);
      setSkipped(result.skipped || []);
      // Pre-select all diffed rows so admin can just click Apply.
      setSelected(new Set(result.diffs.map((_, i) => i)));
    } catch (e: any) {
      setError(e.message || 'Failed to load the sheet.');
      setDiffs(null);
      setSkipped([]);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (i: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const toggleAll = () => {
    if (!diffs) return;
    setSelected(prev =>
      prev.size === diffs.length ? new Set() : new Set(diffs.map((_, i) => i))
    );
  };

  const applySelected = async () => {
    if (!diffs || selected.size === 0) {
      setError('Tick at least one row to apply.');
      return;
    }
    setError(null);
    setApplying(true);
    try {
      const rows = Array.from(selected).map(i => ({
        account: diffs[i].account,
        date: diffs[i].date,
        model_type: diffs[i].sheet.model_type,
        model_value: diffs[i].sheet.model_value,
      }));
      const result = await applySheetPricing(rows);
      setApplyResult(result);
      // Re-diff so the admin sees what's still outstanding.
      const refreshed = await fetchSheetPricingDiff(sheetUrl.trim());
      setDiffs(refreshed.diffs);
      setSkipped(refreshed.skipped || []);
      setSelected(new Set(refreshed.diffs.map((_, i) => i)));
    } catch (e: any) {
      setError(e.message || 'Failed to apply the selected rows.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="sheet-import">
      <div className="sheet-import-header">
        <button onClick={onClose} className="btn btn-secondary">
          ← Back to dashboard
        </button>
        <h2>Import Pricing from Sheet</h2>
      </div>

      <p className="sheet-import-help">
        Paste a Google Sheets URL. The sheet must be shared as "Anyone with the link can view".
        Expected columns: <code>account, date_of_send, model_type, model_value</code>.
      </p>

      <div className="sheet-import-input">
        <input
          type="text"
          value={sheetUrl}
          onChange={e => setSheetUrl(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/.../edit"
          disabled={loading || applying}
          aria-label="Google Sheets URL"
        />
        <button
          onClick={loadDiff}
          className="btn btn-primary"
          disabled={loading || applying || !sheetUrl.trim()}
        >
          {loading ? 'Loading…' : 'Load Diff'}
        </button>
      </div>

      {error && <div className="sheet-import-error">{error}</div>}

      {applyResult && (
        <div className="sheet-import-result">
          <div className="result-applied">
            ✓ Applied {applyResult.applied} change{applyResult.applied === 1 ? '' : 's'}.
          </div>
          {applyResult.failed.length > 0 && (
            <details>
              <summary>
                {applyResult.failed.length} row{applyResult.failed.length === 1 ? '' : 's'} failed
              </summary>
              <ul>
                {applyResult.failed.map((f, i) => (
                  <li key={i}>
                    {f.account ?? '?'} / {f.date ?? '?'}: {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {diffs !== null && diffs.length === 0 && (
        <div className="sheet-import-empty">
          Sheet matches the dashboard — nothing to change.
        </div>
      )}

      {diffs !== null && diffs.length > 0 && (
        <>
          <div className="sheet-import-toolbar">
            <button onClick={toggleAll} className="btn btn-secondary">
              {selected.size === diffs.length ? 'Deselect all' : 'Select all'}
            </button>
            <button
              onClick={applySelected}
              className="btn btn-primary"
              disabled={applying || selected.size === 0}
            >
              {applying
                ? 'Applying…'
                : `Apply ${selected.size} change${selected.size === 1 ? '' : 's'}`}
            </button>
          </div>

          <table className="sheet-import-table">
            <thead>
              <tr>
                <th className="col-check">
                  <input
                    type="checkbox"
                    checked={selected.size === diffs.length}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
                <th>Account</th>
                <th>Date</th>
                <th>Current</th>
                <th>Sheet</th>
              </tr>
            </thead>
            <tbody>
              {diffs.map((d, i) => (
                <tr
                  key={`${d.account}-${d.date}`}
                  className={selected.has(i) ? 'selected' : ''}
                >
                  <td className="col-check">
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => toggleRow(i)}
                    />
                  </td>
                  <td>{formatAccountName(d.account)}</td>
                  <td>{d.date}</td>
                  <td>{formatModelLabel(d.current.model_type, d.current.model_value)}</td>
                  <td className="sheet-value">
                    {formatModelLabel(d.sheet.model_type, d.sheet.model_value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {skipped.length > 0 && (
        <details className="sheet-import-skipped">
          <summary>
            {skipped.length} row{skipped.length === 1 ? '' : 's'} skipped due to validation errors
          </summary>
          <ul>
            {skipped.map((s, i) => (
              <li key={i}>
                Row {s.row_number ?? '?'}
                {s.account ? ` (${s.account})` : ''}: {s.reasons.join('; ')}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
